import React, { Dispatch, SetStateAction, useState } from "react";
import { Box } from "@mui/material";
import History from "../../Assessment/History";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";
import { getAssessments } from "../../../api/assessment";
import { Assessment } from "../../../interfaces/AssessmentInterfaces";
import { useAppDispatch } from "../../../store/hooks";
import * as NotificationActions from "../../../actions/notification";
import { errorFetchMessage, MODULE } from "../../../helpers/messages";
import { Client } from "../../../interfaces/ClientInterface";
import { ASSESSMENT_SEARCH_BY } from "../../Assessment/AssessmentListSearch";
import { filterOpdAssessments } from "../../../helpers/assessmentHelpers";

interface AssessmentState {
  results: Assessment[];
  total: number | null;
}

const sortList = (list: Assessment[]) =>
  list.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime());

interface PropType {
  clientId: number;
  numberOfHistoryToLoad?: number;
  afterFetch?: (list: Assessment[]) => void;
  clientData: Client;
  hidePrescriptionSummaryPrint?: boolean;
  setAssessmentCopy?: Dispatch<SetStateAction<Assessment>>;
}

const ClientAssessments: React.FC<PropType> = ({
  clientId,
  numberOfHistoryToLoad = 20,
  afterFetch,
  clientData,
  hidePrescriptionSummaryPrint,
  setAssessmentCopy
}) => {
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clientAssessments, setClientAssessments] = useState<AssessmentState>({
    results: [],
    total: 0
  });

  React.useEffect(() => {
    if (clientId) {
      (async () => {
        try {
          setLoading(true);
          const res: AssessmentState = await getAssessments({
            searchBy: ASSESSMENT_SEARCH_BY.CLIENT,
            searchValue: String(clientId),
            page,
            pageSize: numberOfHistoryToLoad
          });
          const opdResults = filterOpdAssessments(res.results || []);
          const assessmentList =
            page === 0
              ? sortList(opdResults)
              : clientAssessments.results.concat(sortList(opdResults));
          setClientAssessments({
            results: assessmentList,
            total: res.total
          });
          if (afterFetch) afterFetch(assessmentList);
        } catch (e) {
          dispatch(
            NotificationActions.notificationAdd({
              id: new Date().getUTCMilliseconds(),
              variant: "error",
              message: errorFetchMessage(MODULE.ASSESSMENT),
              autoTimeout: true
            })
          );
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [clientId, page]);

  return (
    <>
      <History
        assessments={clientAssessments.results}
        clientId={clientId}
        clientData={clientData}
        hidePrescriptionSummaryPrint={hidePrescriptionSummaryPrint}
        setAssessmentCopy={setAssessmentCopy}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center"
        }}
      >
        {clientAssessments.results.length !== clientAssessments.total && (
          <StatefulButton
            isLoading={loading}
            circularProgressProps={{ size: 16 }}
            onClick={() => setPage((prevState) => prevState + 1)}
          >
            Load more
          </StatefulButton>
        )}
      </Box>
    </>
  );
};

export default ClientAssessments;
